import React from 'react';
import { DateTime } from 'luxon';
import { usePager } from '../../../helpers/pagination';
import PageIndicator from '../../Common/PageIndicator';
import Row from './row';
import SideBar from '../../Common/SideBar';
import SpotlightHeader from '../../Common/Header';
import './SameStoreSales.scss';
const getFormattedData = (data, metadata) => {
  var _metadata$results;
  const {
    timezone,
    own,
    last_row: last_date
  } = data;
  const results = (_metadata$results = metadata.results) !== null && _metadata$results !== void 0 ? _metadata$results : 10;
  const comparison_date = metadata.boh_comparison_date === 'day' ? 'day' : 'week';
  let column_header = metadata.boh_comparison_date === 'month_week' ? 'Four weeks prior' : `This ${comparison_date} Last Year`;
  if (metadata.boh_comparison_date === 'yesterday_today') {
    column_header = 'Yesterday';
  } else if (metadata.boh_comparison_date === 'prior_weekday') {
    column_header = 'Last week';
  }
  let current_column_header = `Current ${comparison_date}`;
  if (metadata.boh_comparison_date === 'yesterday_today') {
    current_column_header = 'Today';
  } else if (metadata.boh_comparison_date === 'prior_weekday') {
    current_column_header = 'This week';
  }
  const display_dollar_amount = metadata.boh_show_dollar_amount !== false;
  const display_ticket_quantity = metadata.boh_show_ticket_quantity === true;
  const description = getDescription(metadata.boh_comparison_date, timezone, last_date);
  const bohLocations = data.top.slice(0, results);
  const isOwnInRanking = (own === null || own === void 0 ? void 0 : own.ranking) <= results;
  const hide_poweredbyshift = metadata.hide_poweredbyshift;
  return {
    own,
    description,
    bohLocations,
    isOwnInRanking,
    display_dollar_amount,
    display_ticket_quantity,
    column_header,
    current_column_header,
    hide_poweredbyshift
  };
};
const getDescription = (comparison_date, timezone, last_date) => {
  const currentDate = DateTime.local().setZone(timezone);
  const last_date_formatted = last_date ? DateTime.fromISO(last_date).setZone(timezone).toFormat("MM/dd 'at' t") : ' N/A';
  if (comparison_date === 'yesterday_today') {
    const today = currentDate.toFormat('MM/dd/yy');
    const yesterday = currentDate.minus({
      days: 1
    }).toFormat('MM/dd/yy');
    return `Yesterday ${yesterday} vs Today ${today} as of ${last_date_formatted}`;
  }
  if (comparison_date === 'prior_weekday') {
    const weekdayName = currentDate.toFormat('EEEE');
    const thisDay = currentDate.toFormat('MM/dd/yy');
    const lastWeekDay = currentDate.minus({
      days: 7
    }).toFormat('MM/dd/yy');
    return `${weekdayName} ${lastWeekDay} vs ${weekdayName} ${thisDay} as of ${last_date_formatted}`;
  }
  const duration = comparison_date === 'month_week' ? {
    days: 28
  } : {
    years: 1
  };
  const comparison = comparison_date === 'day' ? 'day' : 'week';
  const current_date_from = currentDate.minus({
    hours: 3
  }).startOf(comparison).toFormat('MM/dd/yy');
  const current_date_to = currentDate.toFormat('MM/dd/yy');
  const previous_date_from = currentDate.minus({
    hours: 3
  }).startOf(comparison).minus(duration).toFormat('MM/dd/yy');
  const previous_date_to = currentDate.minus({
    hours: 3
  }).endOf(comparison).minus(duration).toFormat('MM/dd/yy');
  switch (comparison) {
    case 'week':
      return `Week sales ${previous_date_from} - ${previous_date_to} vs ${current_date_from} - ${current_date_to} as of ${last_date_formatted}`;
    case 'day':
      return `Daily sales ${previous_date_from} vs ${current_date_from} at ${last_date_formatted}`;
    default:
      return '';
  }
};
const SameStoreSales = props => {
  const {
    data,
    metadata,
    text,
    duration,
    playing,
    bohStyle
  } = props;
  const {
    accent_color,
    title_font_family,
    text_accent_color,
    containerStyle,
    listHeaderRowStyle
  } = bohStyle;
  const {
    bohLocations,
    own,
    isOwnInRanking,
    description,
    current_column_header,
    column_header,
    display_dollar_amount,
    hide_poweredbyshift,
    display_ticket_quantity
  } = getFormattedData(data, metadata);
  const [page, pageIdx, pages] = usePager(bohLocations, duration * 1000, playing);
  const Header = /*#__PURE__*/React.createElement(SpotlightHeader, {
    text: text,
    metadata: metadata,
    description: description,
    isLeaderboard: true
  });
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: "sales-container",
    style: containerStyle
  }, /*#__PURE__*/React.createElement("div", {
    className: "width-container"
  }, Header, /*#__PURE__*/React.createElement("div", {
    className: "ticket-list"
  }, /*#__PURE__*/React.createElement("div", {
    className: "list-row list-header",
    style: listHeaderRowStyle
  }, /*#__PURE__*/React.createElement("p", {
    className: "header-location"
  }, "Location"), display_dollar_amount && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("p", {
    className: "header-previous"
  }, column_header), /*#__PURE__*/React.createElement("p", {
    className: "header-current"
  }, current_column_header)), /*#__PURE__*/React.createElement("p", {
    className: "header-variation"
  }, "Variation"), display_ticket_quantity && /*#__PURE__*/React.createElement("p", {
    className: "header-ticket-quantity"
  }, "Tickets")), page.map((loc, index) => /*#__PURE__*/React.createElement(Row, {
    key: `${loc.location}=${index}`,
    location: loc,
    own: own,
    accent_color: accent_color,
    display_dollar_amount: display_dollar_amount,
    display_ticket_quantity: display_ticket_quantity
  })), !!own && !isOwnInRanking && /*#__PURE__*/React.createElement(Row, {
    key: `${own.location}`,
    location: own,
    own: own,
    accent_color: accent_color,
    display_dollar_amount: display_dollar_amount,
    display_ticket_quantity: display_ticket_quantity
  }), /*#__PURE__*/React.createElement("div", {
    className: "results-description"
  }, "Results ordered by Variance")), pages.length > 1 && /*#__PURE__*/React.createElement(PageIndicator, {
    pages: pages,
    current: pageIdx,
    selectedColor: accent_color
  }))), /*#__PURE__*/React.createElement(SideBar, {
    accent_color: accent_color,
    accent_text_color: text_accent_color,
    logo: metadata.logo,
    text: text.message_board,
    fontFamily: title_font_family,
    hide_poweredbyshift: hide_poweredbyshift
  }));
};
export default SameStoreSales;